import React, { useState, useEffect, useContext } from 'react';
import CartContext from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { productAPI } from '../utils/api';

const CatalogPage = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loading, setLoading] = useState(true);
    const { addToCart } = useContext(CartContext);

    const categories = ['all', 'Athletic Tapes', 'Medical Grade', 'Support & Recovery'];

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await productAPI.getAll();
                setProducts(response.data);
                setFilteredProducts(response.data);
            } catch (err) {
                console.error('Failed to fetch products:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        if (selectedCategory === 'all') {
            setFilteredProducts(products);
        } else {
            setFilteredProducts(products.filter(p => p.category === selectedCategory));
        }
    }, [selectedCategory, products]);

    const handleAddToCart = (productId, quantity = 1, variantId = null) => addToCart(productId, quantity, variantId);

    return (
        <div className="overflow-x-hidden">
            {/* ── HERO ──────────────────────────────────────────── */}
            <section
                className="relative pt-36 pb-20 overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #0a0a14 0%, #0f172a 50%, #1e1b4b 100%)' }}
            >
                <div className="absolute top-1/2 right-1/4 w-80 h-80 rounded-full opacity-15 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #3b82f6 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative max-w-7xl mx-auto px-6">
                    <div className="inline-flex items-center gap-2 border border-blue-500/40 rounded-full px-4 py-1.5 text-blue-300 text-xs font-medium tracking-widest mb-6"
                        style={{ background: 'rgba(59,130,246,0.12)', backdropFilter: 'blur(8px)' }}>
                        <span className="w-2 h-2 rounded-full bg-blue-400" />
                        PRODUCT CATALOGUE
                    </div>
                    <h1 className="text-6xl sm:text-7xl font-bold text-white mb-4 tracking-tight">Products</h1>
                    <p className="text-xl text-slate-300 font-light max-w-xl leading-relaxed">
                        Tape, bandages and pitchside kit for physios, trainers and athletes. Free delivery nationwide.
                    </p>
                </div>

                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
                        <path d="M0 50L80 43C160 36 320 22 480 16C640 10 800 16 960 22C1120 28 1280 34 1360 37L1440 40V0H0Z" fill="#f8fafc" />
                    </svg>
                </div>
            </section>

            <div className="min-h-screen bg-slate-50 pb-24">
                <div className="max-w-7xl mx-auto px-6 pt-12">
                    {/* Category Filter */}
                    <div className="flex flex-wrap gap-3 mb-12">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className="px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300"
                                style={selectedCategory === cat
                                    ? { background: 'linear-gradient(135deg, #2563eb, #4f46e5)', color: '#fff', boxShadow: '0 4px 16px rgba(99,102,241,0.3)' }
                                    : { background: '#fff', color: '#475569', border: '1px solid #e2e8f0' }
                                }
                            >
                                {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="rounded-3xl overflow-hidden bg-white">
                                    <div className="shimmer h-56 w-full" />
                                    <div className="p-6 space-y-3">
                                        <div className="shimmer h-4 w-3/4 rounded-full" />
                                        <div className="shimmer h-3 w-full rounded-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-24">
                            <div className="w-20 h-20 rounded-3xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
                                <svg className="w-9 h-9 text-slate-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                                </svg>
                            </div>
                            <p className="text-slate-500 font-light text-lg">No products found in this category.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredProducts.map(product => (
                                <ProductCard key={product._id} product={product} onAddToCart={handleAddToCart} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CatalogPage;
